import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Home,
  Car,
  Wrench,
  MapPin,
  CreditCard,
  ShieldCheck,
  Info,
  LogIn,
  UserPlus,
  LogOut,
  MessageCircle,
  User as UserIcon,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuthState } from "@/context/useAuthState";
import { FIREBASE_AUTH } from "@/firebase";
import { signOut } from "firebase/auth";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

type MobileNavItem = {
  label: string;
  icon: typeof Home;
  to?: string;
  action?: () => void;
  badge?: number;
};

/**
 * Header dedicado para móvil: barra sólida fija, logo grande centrado
 * y menú lateral. Sin efectos costosos (backdrop-blur / animaciones de
 * pintura) para garantizar apertura instantánea del menú.
 */
export function MobileHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useAuthState();

  const close = () => setOpen(false);
  const isFinancing = location.pathname === "/financiamiento";

  const go = (to: string) => {
    close();
    navigate(to);
  };

  const handleSignOut = async () => {
    try {
      await signOut(FIREBASE_AUTH);
      setUser(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
    close();
  };

  const navItems: MobileNavItem[] = [
    { label: "Inicio", icon: Home, to: "/" },
    { label: "Catálogo", icon: Car, to: "/catalogo" },
    { label: "Repuestos", icon: Wrench, to: "/repuestos" },
    { label: "Sucursales", icon: MapPin, to: "/sucursales" },
    { label: "Financiamiento", icon: CreditCard, to: "/financiamiento" },
    { label: "Garantía", icon: ShieldCheck, to: "/garantia" },
    { label: "Nosotros", icon: Info, to: "/nosotros" },
  ];

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 h-16 bg-[#1F2240] shadow-[0_4px_20px_rgba(0,0,0,0.28)] lg:hidden">
        <div className="relative flex h-full items-center justify-between px-3">
          {/* Menú */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Abrir menú de navegación"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition-colors active:bg-white/25"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Logo centrado, grande, sobresale hacia abajo como insignia */}
          <Link
            to="/"
            aria-label="Inicio Oriental Ramirez"
            className="absolute left-1/2 top-1.5 -translate-x-1/2"
          >
            <span className="flex h-[74px] w-[74px] items-center justify-center rounded-full bg-white p-1.5 shadow-[0_10px_28px_rgba(225,15,24,0.4)] ring-[3px] ring-[#E10F18]">
              <img
                src="/logo.png"
                alt=""
                className="h-full w-full rounded-full object-cover"
              />
            </span>
          </Link>

          {/* Extremo derecho: WhatsApp en financiamiento, si no espaciador */}
          {isFinancing ? (
            <a
              href={buildWhatsAppUrl(
                "Hola, quiero información sobre el financiamiento de vehículos."
              )}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contactar por WhatsApp"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_4px_14px_rgba(37,211,102,0.5)] transition-transform active:scale-95"
            >
              <MessageCircle className="h-5 w-5" />
            </a>
          ) : (
            <div className="h-11 w-11" aria-hidden="true" />
          )}
        </div>
      </header>

      {/* Menú lateral */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="left"
          className="w-[300px] border-r border-white/10 bg-[#1F2240] p-0 [&>button]:hidden"
        >
          <div className="flex h-full flex-col">
            {/* Encabezado del menú */}
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white ring-2 ring-[#E10F18]">
                  <img src="/logo.png" alt="" className="h-full w-full rounded-full object-cover p-0.5" />
                </span>
                <div className="leading-tight">
                  <p className="text-sm font-bold text-white">Oriental Ramirez</p>
                  <p className="text-[11px] text-yellow-200/80">Vehículos y repuestos</p>
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Cerrar menú"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition-colors active:bg-white/10"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Navegación */}
            <nav className="flex-1 overflow-y-auto p-3">
              <div className="grid gap-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = item.to && location.pathname === item.to;
                  const content = (
                    <>
                      <Icon className="h-5 w-5 shrink-0" />
                      <span className="flex-1">{item.label}</span>
                      {item.badge != null && item.badge > 0 && (
                        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#E10F18] px-1.5 text-xs font-bold text-white">
                          {item.badge > 99 ? "99+" : item.badge}
                        </span>
                      )}
                    </>
                  );
                  const cls = `flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-[15px] font-semibold transition-colors ${
                    active
                      ? "bg-[#E10F18] text-white"
                      : "text-white/85 active:bg-white/10"
                  }`;

                  return item.to ? (
                    <Link key={item.label} to={item.to} onClick={close} className={cls}>
                      {content}
                    </Link>
                  ) : (
                    <button key={item.label} type="button" onClick={item.action} className={cls}>
                      {content}
                    </button>
                  );
                })}
              </div>
            </nav>

            {/* Sesión */}
            <div className="border-t border-white/10 p-3">
              {user ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-1 text-sm text-white/80">
                    <UserIcon className="h-4 w-4" />
                    <span className="truncate">{user.email?.split("@")[0]}</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/20 px-4 py-2.5 text-sm font-semibold text-white transition-colors active:bg-white/10"
                  >
                    <LogOut className="h-4 w-4" />
                    Cerrar sesión
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => go("/login")}
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-white/20 px-3 py-2.5 text-sm font-semibold text-white transition-colors active:bg-white/10"
                  >
                    <LogIn className="h-4 w-4" />
                    Acceder
                  </button>
                  <button
                    type="button"
                    onClick={() => go("/registro")}
                    className="flex items-center justify-center gap-1.5 rounded-xl bg-[#E10F18] px-3 py-2.5 text-sm font-semibold text-white transition-colors active:bg-[#c00d15]"
                  >
                    <UserPlus className="h-4 w-4" />
                    Registro
                  </button>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
